import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { MenuProps } from 'antd-v5';
import { Badge, Popover } from 'antd-v5';
import { RootState } from 'app/AppStore';
import { getLinkItems } from 'components/Page/Header/TopHeader/Links/LinkTabs';
import { getMenuItems } from 'components/Page/PageSideBar/PageSideBar';
import {
  getAscOrdersEnabled,
  getClaimsEnabled,
  getDepositReturnListEnabled,
  getExadisReturnListEnabled,
  getTrophyAdvantagesLink,
  getVirtualStoreBamLink,
} from 'domains/appContext/AppContext.store';
import { getCurrentEstimateId, getRowTablesCount } from 'domains/estimate/Estimate.store';
import { getCurrentPromotionsData } from 'domains/promotion/Promotion.store';
import { getUserCountry, getUserNotifications, getUserRights, UserRole } from 'domains/user';
import { theme } from 'styles';
import { Flex, WithTooltip } from 'UI';
import { getData, getSearchData, hasUserAnyRight } from 'utils';
import { Country } from 'utils/i18n/Country';

type MenuItem = NonNullable<Required<MenuProps>['items'][number]> & {
  'data-cy'?: string;
};
type SidebarMenuItem = ReturnType<typeof getMenuItems>[number];
type HeaderLinkItem = ReturnType<typeof getLinkItems>[number];

export interface R1MenuNavigation {
  link?: string;
  isExternal: boolean;
  parentKey?: string;
  hasOverlay?: boolean;
}

export type R1MenuNavigationMap = Map<string, R1MenuNavigation>;

export interface R1SidebarMenu {
  items: MenuItem[];
  navigationMap: R1MenuNavigationMap;
}

interface R1MenuSourceItem {
  label?: string;
  text?: string;
  link?: string;
  externalLink?: string;
  'data-cy'?: string;
  key?: string;
  r1MenuIcon?: React.ReactNode;
  popinOverlay?: React.ReactNode;
  children?: R1MenuSourceItem[];
}

interface R1MenuDividerItem {
  key: string;
  type: 'divider';
}

type R1MenuOrderedItem = R1MenuSourceItem | R1MenuDividerItem;

const isR1MenuDividerItem = (item: R1MenuOrderedItem): item is R1MenuDividerItem =>
  'type' in item && item.type === 'divider';

const collectNavigation = (item: R1MenuOrderedItem, navigationMap: R1MenuNavigationMap, parentKey?: string): void => {
  if (isR1MenuDividerItem(item)) {
    return;
  }
  const key = item?.key;
  if (key) {
    navigationMap.set(key, {
      link: item.link ?? item.externalLink,
      isExternal: Boolean(item.externalLink),
      parentKey,
      hasOverlay: Boolean(item.popinOverlay),
    });
  }
  item.children?.forEach((child) => collectNavigation(child, navigationMap, key));
};

export interface R1MenuBadgeData {
  count?: number;
  color?: string;
  fontColor?: string;
}

const toR1MenuItem = (
  collapsed: boolean,
  openOverlayKey: string | undefined,
  onOverlayOpenChange: (key: string, open: boolean) => void,
  badgeData: Record<string, R1MenuBadgeData>,
  isChild = false,
) => (item: R1MenuOrderedItem): MenuItem => {
  if (isR1MenuDividerItem(item)) {
    return item;
  }

  const key = item?.key ?? '/';
  const dataCy = item['data-cy'] ?? key;

  const children = item.children?.map(toR1MenuItem(collapsed, openOverlayKey, onOverlayOpenChange, badgeData, true));
  const textLabel = item.label ?? item.text;
  const collapsedLabel = collapsed && !isChild;

  const withOverlay = (content: React.ReactNode): React.ReactNode =>
    item.popinOverlay ? (
      <Popover
        content={item.popinOverlay}
        placement={'rightBottom'}
        color={theme.color.white}
        trigger={'click'}
        open={openOverlayKey === key}
        onOpenChange={(open) => onOverlayOpenChange(key, open)}
      >
        {content}
      </Popover>
    ) : (
      <Flex>{content}</Flex>
    );

  const badge = badgeData[key];
  const withBadge = (content: React.ReactNode): React.ReactNode =>
    badge?.count ? (
      <Flex data-cy={dataCy} align={'center'} justify={'space-between'} minWidth={'100%'} gap={8}>
        {content}
        <Badge
          count={badge.count}
          color={badge.color}
          size={'small'}
          data-cy={'badge'}
          styles={{ indicator: { color: badge.fontColor, fontWeight: 'bold', padding: '0 3px' } }}
        />
      </Flex>
    ) : (
      content
    );

  const baseLabel = collapsedLabel ? (
    <WithTooltip title={textLabel}>
      {withOverlay(
        <Flex data-cy={dataCy} align={'center'} justify={'center'} minHeight={40} maxHeight={40}>
          {item.r1MenuIcon}
        </Flex>,
      )}
    </WithTooltip>
  ) : (
    withOverlay(withBadge(textLabel))
  );

  return {
    key,
    label: baseLabel,
    'data-cy': dataCy,
    ...(children?.length ? { children } : {}),
  };
};

const findByKey = (items: SidebarMenuItem[], key: string) => items.find((item) => item.key === key);

const getOrderedR1MenuItems = (
  menuItems: SidebarMenuItem[],
  linkItems: HeaderLinkItem[],
): Array<R1MenuOrderedItem | undefined> => [
  findByKey(menuItems, 'tab-home'),
  findByKey(menuItems, 'tab-profile-settings'),
  findByKey(menuItems, 'tab-my-orders'),
  findByKey(menuItems, 'tab-as-drive'),
  findByKey(menuItems, 'tab-my-network'),
  { key: 'r1-divider-after-my-network', type: 'divider' },
  ...linkItems,
  findByKey(menuItems, 'tab-estimate'),
  { key: 'r1-divider-after-estimate', type: 'divider' },
  findByKey(menuItems, 'tab-notifications'),
  findByKey(menuItems, 'link-virtual-store-bam'),
  findByKey(menuItems, 'tab-settings'),
];

const isR1MenuOrderedItem = (item: R1MenuOrderedItem | undefined): item is R1MenuOrderedItem => Boolean(item);

export const useR2SidebarMenuItems = (collapsed: boolean): R1SidebarMenu => {
  const { t } = useTranslation();
  const userRights = useSelector(getUserRights);
  const trophyAdvantagesUrl = useSelector(getTrophyAdvantagesLink);
  const virtualStoreBamUrl = useSelector(getVirtualStoreBamLink);
  const userCountry = useSelector(getUserCountry);
  const country = useSelector(getUserCountry);
  const promotionsData = useSelector(getCurrentPromotionsData);
  const showCurrentPromotions = promotionsData?.length > 0;
  const showTires = Country.canShowTiresByCountry(country);
  const ascOrdersEnabled = useSelector(getAscOrdersEnabled) ?? false;
  const claimsEnabled = useSelector(getClaimsEnabled) ?? false;
  const exadisReturnListEnabled = useSelector(getExadisReturnListEnabled) ?? false;
  const depositReturnListEnabled = useSelector(getDepositReturnListEnabled) ?? false;
  const menuItems = getMenuItems(
    t,
    getData(userRights) ?? [],
    userCountry,
    trophyAdvantagesUrl,
    virtualStoreBamUrl,
    ascOrdersEnabled,
    claimsEnabled,
    exadisReturnListEnabled,
    depositReturnListEnabled,
  );
  const linkItems = getLinkItems(t, showTires, showCurrentPromotions, 'sidebar');

  const notificationCount = getSearchData(useSelector(getUserNotifications))?.notifications?.length;
  const currentEstimateId = useSelector(getCurrentEstimateId);
  const estimateRowsCount = useSelector((state: RootState) => getRowTablesCount(state, currentEstimateId));
  const badgeData: Record<string, R1MenuBadgeData> = {
    'tab-notifications': { count: notificationCount },
    'tab-estimate': { count: estimateRowsCount, color: theme.color.brand, fontColor: theme.color.brand_black },
  };

  const [openOverlayKey, setOpenOverlayKey] = useState<string | undefined>(undefined);
  const onOverlayOpenChange = (key: string, open: boolean) => setOpenOverlayKey(open ? key : undefined);

  const navigationMap: R1MenuNavigationMap = new Map();
  const orderedItems = getOrderedR1MenuItems(menuItems, linkItems).filter(isR1MenuOrderedItem);
  orderedItems.forEach((item) => collectNavigation(item, navigationMap));
  const items = orderedItems.map(toR1MenuItem(collapsed, openOverlayKey, onOverlayOpenChange, badgeData));

  return { items, navigationMap };
};

export const useShowR1SidebarMenu = () => {
  const userRights = useSelector(getUserRights);
  return hasUserAnyRight([UserRole.R1_ORDER_AND_CLAIM, UserRole.R1], getData(userRights));
};
